/**
 * Environment-aware logging utility for frontend
 * Production: Simple event logs only
 * Development: Full detailed logs
 */

const isDevelopment = process.env.NODE_ENV === 'development';

/**
 * Log a simple event (always logged, minimal info)
 * @param {string} event - Event name (e.g., 'recipe.created', 'fork.created', 'session.started')
 * @param {object} metadata - Minimal metadata (only IDs and essential info)
 */
export function logEvent(event, metadata = {}) {
    const timestamp = new Date().toISOString();
    const logData = {
        timestamp,
        event,
        ...metadata
    };
    console.log(`[EVENT] ${event}`, JSON.stringify(logData));
}

/**
 * Log detailed debug information (development only)
 * @param {string} context - Context/location (e.g., '[CreateRecipe]')
 * @param {string} message - Log message
 * @param {object} data - Full data object (only logged in development)
 */
export function logDebug(context, message, data = null) {
    if (isDevelopment) {
        if (data !== null) {
            console.log(`${context} ${message}`, data);
        } else {
            console.log(`${context} ${message}`);
        }
    }
}

/**
 * Log error (always logged, but with different detail levels)
 * @param {string} context - Context/location
 * @param {string} message - Error message
 * @param {Error|object} error - Error object or data
 */
export function logError(context, message, error = null) {
    if (isDevelopment) {
        // Full error details in development
        if (error instanceof Error) {
            console.error(`${context} ${message}`, error);
        } else if (error) {
            console.error(`${context} ${message}`, error);
        } else {
            console.error(`${context} ${message}`);
        }
    } else {
        // Minimal error info in production
        const errorInfo = error instanceof Error 
            ? { message: error.message, name: error.name }
            : error;
        console.error(`[ERROR] ${context} ${message}`, errorInfo ? JSON.stringify(errorInfo) : '');
    }
}

/**
 * Log warning (always logged)
 * @param {string} context - Context/location
 * @param {string} message - Warning message
 * @param {object} data - Optional data (only in development)
 */
export function logWarn(context, message, data = null) {
    if (isDevelopment && data) {
        console.warn(`${context} ${message}`, data);
    } else {
        console.warn(`[WARN] ${context} ${message}`);
    }
}

